/* eslint-disable prettier/prettier */
import { all, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { songService } from 'services/api';
import type { HTTPError, ISongPeaks, ITag } from 'services/api/entities';
import {
  CustomizableSong,
  FreeSong,
  PaginatedList,
} from 'services/api/entities';
import { getWaveformData } from 'services/helpers/song';
import { errorAction, startAction, stopAction } from '../base/actions';
import { fetchAndCombineSongsData } from '../helpers/fetchAndCombineSongsData';
import { actions } from '../newSongs';
import { getSongSuccess, getSongsSuccess } from './actions';
import songsActions from './actions/constants';
import { Song } from './types';

/**
 * Request the songs lists of a specific page
 * @param action
 */
export function* asyncGetSong(action: {
  type: string;
  payload: { songId: string };
}) {
  const { songId } = action.payload;

  const { storeSongLoading, storeSongError, storeSongSuccess } = actions;
  yield put(startAction(action));
  yield put(storeSongLoading({ songId }));
  try {
    const song: FreeSong | CustomizableSong = yield songService.getSong(songId);
    const songPeaks: ISongPeaks | null = yield getWaveformData(song);

    const newSong: Song = { ...song, peaks: songPeaks };

    yield put(storeSongSuccess({ song: newSong }));
    yield put(getSongSuccess({ song: newSong }));
  } catch (error) {
    yield put(storeSongError({ songId, error: error as HTTPError }));
    yield put(errorAction({ type: action.type, payload: error as HTTPError }));
  } finally {
    yield put(stopAction(action));
  }
}

/**
 * Request the songs lists of a specific page
 * @param action
 */
export function* asyncGetSongs(action: {
  type: string;
  payload: {
    page: number;
    selectedTags: Record<string, ReadonlyArray<ITag>>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    appliedFilters: Record<string, any>;
  };
}) {
  const { page, selectedTags, appliedFilters } = action.payload;
  yield put(startAction(action));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filters: Record<string, any> = {};
  Object.keys(selectedTags).forEach((key: string) => {
    if (key === 'vocals') {
      if (selectedTags[key] && selectedTags[key].length === 0) return;
      // has_vocal is a single true/false query parameter thus
      // we need to extract the first element and get it's queryValue
      filters.has_vocal = selectedTags[key][0].queryValue;
    } else {
      filters[key] = selectedTags[key]?.map((tag: ITag) => tag.queryValue);
    }
  });

  Object.keys(appliedFilters).forEach((key: string) => {
    // This will `return` if the [key] is `false` or `null`. It's ok for now since the design
    // expects only 2 values (nothing or `true`) but if in the future we would want
    // to have a 3 value (`false` - get only non customizable songs) then this statement
    // will need to change
    if (!appliedFilters[key]) return;
    filters[key] = appliedFilters[key];
  });

  try {
    const songList: PaginatedList<FreeSong | CustomizableSong> =
      yield songService.getSongList({
        page,
        ...filters,
      });

    const songs = songList.items;

    const songsObj: Record<string, Song> = yield fetchAndCombineSongsData(
      songs
    );

    const { storeSongs } = actions;
    yield put(storeSongs({ songs: songsObj }));
    yield put(
      getSongsSuccess({
        songs: songsObj,
        paginationData: songList.paginationData,
      })
    );
  } catch (error) {
    yield put(errorAction({ type: action.type, payload: error as HTTPError }));
  } finally {
    yield put(stopAction(action));
  }
}

/* ----------------------------------------------------------------------------------------
 * Watch sagas
 * ----------------------------------------------------------------------------------------*/

export function* watchGetSong() {
  yield takeEvery(songsActions.GET_SONG, asyncGetSong);
}

export function* watchGetSongs() {
  yield takeLatest(songsActions.GET_SONGS, asyncGetSongs);
}

export default function* rootSaga() {
  yield all([watchGetSong(), watchGetSongs()]);
}
